import { formatPostCode } from '@avantia/client-and-server-utilities';
import { Form } from 'react-bootstrap';
import { useState, useEffect } from 'react';
import { shallowEqual } from 'react-redux';

import { sendAuthLinkAction } from 'actions/sendAuthLinkAction';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { TextInputControl } from 'components/shared/textInputControl';
import { Button } from 'components/shared/button';

import './loginForm.scss';
import type { LoginOptions } from '../loginPage';
import { LoginError } from './loginError';
import { analyticsUserType, GTM_EVENT_TYPES, sendAnalytics } from 'analytics';

export interface LoginFormProps {
  title: string;
  helperText: JSX.Element;
  source: LoginOptions;
  path?: string;
}

export const LoginForm = ({ title, helperText, source, path }: LoginFormProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const {
    loginValidationErrors,
    loginError,
    authLinkFailed,
    customerInsuranceSummaryError,
    authLinkError,
    isTokenError,
    isPolicyDetailsError,
    policyDetailsError,
    authType
  } = useAppSelector(
    ({
      login: { loginError, loginValidationErrors, authLinkFailed },
      customerData: { customerInsuranceSummaryError, isPolicyDetailsError, policyDetailsError },
      authentication: { authLinkError, isTokenError, authType }
    }) => ({
      loginError,
      loginValidationErrors,
      authLinkFailed,
      customerInsuranceSummaryError,
      authLinkError,
      isTokenError,
      isPolicyDetailsError,
      policyDetailsError,
      authType
    }),
    shallowEqual
  );

  const [form, setForm] = useState({
    emailAddress: '',
    postcode: '',
    source
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handlePostcodeBlur = (e) => {
    setForm({ ...form, postcode: formatPostCode(e.target.value) });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(sendAuthLinkAction(form));
  };

  useEffect(() => {
    sendAnalytics({
      event: GTM_EVENT_TYPES.PAGE_VIEW,
      detail: {
        virtualpage_title: `Homeprotect • ${title}`,
        virtualpage: path,
        virtualpage_url: document.location.href,
        usertype: analyticsUserType(authType === 'agent')
      }
    });
  }, []);

  return (
    <Form onSubmit={handleSubmit} className="login-form form-group">
      {authLinkFailed && <LoginError error={loginError} />}
      {customerInsuranceSummaryError && !isTokenError && <LoginError error={authLinkError} />}
      {isPolicyDetailsError && <LoginError error={policyDetailsError} />}
      <h2 className="login-title">{title}</h2>
      {helperText}
      <TextInputControl
        label="Email"
        placeholder="name@email.com"
        value={form.emailAddress}
        handleChange={handleChange}
        name="emailAddress"
        error={loginValidationErrors && loginValidationErrors.emailAddress}
      />
      <TextInputControl
        label="Postcode"
        placeholder="e.g. EC4Y 8EN"
        value={form.postcode}
        handleBlur={handlePostcodeBlur}
        handleKeyDown={(e) => e.key === 'Enter' && handlePostcodeBlur(e)}
        handleChange={handleChange}
        name="postcode"
        error={loginValidationErrors && loginValidationErrors.postcode}
        fullWidth={false}
      />
      <Button text="EMAIL MY ACCESS LINK" type="submit" id="email-access-link" />
    </Form>
  );
};
